import React from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {THEMES} from '../persistence/quoteTemplateSchema';
import {themeTokens} from '../theme/themeTokens';
import {GroupLabel, LabelledToggle, Section, Sections, SettingStack} from './common';
import type {PanelProps} from './types';
import type {QuoteTemplate, QuoteTheme} from '../resolver/types';

const ThemeGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 8px;
	margin-bottom: 16px;
`;

const ThemeCard = styled.button<{$selected: boolean}>`
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 10px;
	border: 1px solid
		${({theme, $selected}) => ($selected ? theme.sys.color.brand.primary['1'] : theme.sys.color.border.decorative)};
	border-radius: 8px;
	background: ${({theme, $selected}) =>
		$selected ? theme.sys.color.background.primary['1'] : theme.sys.color.background.neutral['1']};
	cursor: pointer;
	text-align: left;
`;

/**
 * A miniature of the page the theme produces, drawn from the same tokens the renderers use: the
 * cover band, a heading rule and two body lines.
 */
const Thumb = styled.span`
	display: block;
	height: 56px;
	border: 1px solid ${({theme}) => theme.sys.color.border.decorative};
	border-radius: 4px;
	background: #fff;
	overflow: hidden;
`;

const ThumbBand = styled.span<{$background: string}>`
	display: block;
	height: 16px;
	background: ${({$background}) => $background};
`;

const ThumbRule = styled.span<{$color: string}>`
	display: block;
	margin: 6px 8px 0;
	height: 4px;
	border-radius: 2px;
	background: ${({$color}) => $color};
`;

const ThumbLine = styled.span`
	display: block;
	margin: 5px 8px 0;
	height: 3px;
	border-radius: 2px;
	background: ${({theme}) => theme.sys.color.border.decorative};
`;

const ThemeName = styled.span`
	font-size: 13px;
`;

const THEME_LABEL_ID: Record<QuoteTheme, string> = {
	modern: 'quote_designer.theme.modern',
	classic: 'quote_designer.theme.classic',
	minimal: 'quote_designer.theme.minimal',
};

/** Document-level presentation: the theme preset, and whether each page carries a header/footer. */
const LayoutThemePanel: React.FC<PanelProps> = ({template, onChange}) => {
	const intl = useIntl();
	const setLayout = (patch: Partial<QuoteTemplate['layout']>) =>
		onChange({...template, layout: {...template.layout, ...patch}});

	return (
		<Sections $gap={18}>
			<Section>
				<GroupLabel>{intl.formatMessage({id: 'quote_designer.theme.label'})}</GroupLabel>
				<ThemeGrid role="radiogroup" aria-label={intl.formatMessage({id: 'quote_designer.theme.label'})}>
					{THEMES.map(theme => {
						const selected = template.layout.theme === theme;
						// Only the chosen card previews in the accent; the rest read as neutral thumbnails,
						// so the selection is legible without relying on the border alone.
						const tokens = themeTokens(theme, template.branding.accentColor, template.branding.font);
						return (
							<ThemeCard
								key={theme}
								type="button"
								role="radio"
								aria-checked={selected}
								$selected={selected}
								onClick={() => setLayout({theme})}
								data-cy={`quote-theme-${theme}`}
							>
								<Thumb aria-hidden="true">
									<ThumbBand $background={selected ? tokens.coverBackground : '#e7e7f0'} />
									<ThumbRule $color={selected ? tokens.accent : '#e7e7f0'} />
									<ThumbLine />
									<ThumbLine />
								</Thumb>
								<ThemeName>{intl.formatMessage({id: THEME_LABEL_ID[theme]})}</ThemeName>
							</ThemeCard>
						);
					})}
				</ThemeGrid>
			</Section>

			<Section>
				<GroupLabel>{intl.formatMessage({id: 'quote_designer.layout.page_elements'})}</GroupLabel>
				<SettingStack>
					<LabelledToggle
						label={intl.formatMessage({id: 'quote_designer.layout.page_header'})}
						description={intl.formatMessage({id: 'quote_designer.layout.page_header_hint'})}
						checked={template.layout.pageHeader}
						onChange={checked => setLayout({pageHeader: checked})}
						cy="quote-toggle-page-header"
					/>
					<LabelledToggle
						label={intl.formatMessage({id: 'quote_designer.layout.page_footer'})}
						description={intl.formatMessage({id: 'quote_designer.layout.page_footer_hint'})}
						checked={template.layout.pageFooter}
						onChange={checked => setLayout({pageFooter: checked})}
						cy="quote-toggle-page-footer"
					/>
				</SettingStack>
			</Section>
		</Sections>
	);
};

export default LayoutThemePanel;
